/**
 * Calculate Euclidean distance between two points in pixel space
 * @param {Object} point1 - {x, y} in pixels
 * @param {Object} point2 - {x, y} in pixels
 * @returns {number} Distance in pixels
 */
const calculatePixelDistance = (point1, point2) => {
  const dx = point2.x - point1.x
  const dy = point2.y - point1.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Calculate meters per pixel ratio from geographic bounds
 * @param {Array} bounds - [minLng, minLat, maxLng, maxLat]
 * @param {number} imageWidth - Image width in pixels (default 2000)
 * @returns {number} Meters per pixel ratio
 */
const calculateMetersPerPixel = (bounds, imageWidth = 2000) => {
  if (!bounds || bounds.length !== 4) return null

  const [minLng, minLat, maxLng, maxLat] = bounds

  // Geographic width/height in degrees
  const lngDegrees = maxLng - minLng
  const latDegrees = maxLat - minLat

  // Convert to meters using WGS84 approximation
  const centerLat = (minLat + maxLat) / 2
  const latRad = (centerLat * Math.PI) / 180

  // Meters per degree at this latitude
  const metersPerDegreeLng = 111320 * Math.cos(latRad)
  const metersPerDegreeLat = 110574

  // Total width/height in meters
  const widthMeters = lngDegrees * metersPerDegreeLng
  const heightMeters = latDegrees * metersPerDegreeLat

  // Average meters per pixel (use width since image is square)
  const metersPerPixel = widthMeters / imageWidth

  return metersPerPixel
}

/**
 * Calculate distance in meters between two points using climate bounds
 * @param {Object} point1 - {x, y} in pixel coordinates
 * @param {Object} point2 - {x, y} in pixel coordinates
 * @param {Array} bounds - [minLng, minLat, maxLng, maxLat] from climate data
 * @param {number} imageWidth - Image width in pixels (default 2000)
 * @returns {number} Distance in meters
 */
export const calculateGeographicDistance = (point1, point2, bounds, imageWidth = 2000) => {
  // Calculate pixel distance
  const pixelDistance = calculatePixelDistance(point1, point2)

  // If no bounds, return pixel distance (fallback)
  if (!bounds) {
    return pixelDistance
  }

  // Convert to meters
  const metersPerPixel = calculateMetersPerPixel(bounds, imageWidth)
  if (!metersPerPixel) {
    return pixelDistance
  }

  return pixelDistance * metersPerPixel
}

/**
 * Format distance for display — delegates to measurement-utils for unit-aware formatting.
 * @param {number} meters - Distance in meters
 * @param {string} [unit='m'] - Display unit: 'm' or 'ft'
 * @returns {string} Formatted distance
 */
export { formatDistance } from '~/common/measurement-utils'
